/* eslint-disable i18next/no-literal-string */
import {Badge} from '@dropbox/dig-components/badge';
import {Button} from '@dropbox/dig-components/buttons';
import {List} from '@dropbox/dig-components/list';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {EditLine, ImageLine} from '@dropbox/dig-icons/assets';
import type {AdminHomeBanner} from 'client';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {getBackendUrl} from 'utilities';

import {BannerStatus, bannerStatus, formatSchedule, scheduleZoneLabel} from './bannerSchedule';
import styles from './HomeBanners.module.css';

const STATUS = {
  scheduled: {label: 'Scheduled', variant: 'attention'},
  live: {label: 'Live', variant: 'success'},
  ended: {label: 'Ended', variant: 'standard'},
} as const satisfies Record<BannerStatus, {label: string; variant: string}>;

export const BannerList = ({
  banners,
  now,
  onEdit,
}: {
  banners: AdminHomeBanner[];
  now: Date;
  onEdit: (banner: AdminHomeBanner) => void;
}) => (
  <List spacing="medium" verticalAlign="center" padding="0" aria-label="Home banners">
    {banners.map((banner) => {
      const status = STATUS[bannerStatus(banner, now)];
      return (
        <List.Item key={banner.id} className={atoms({borderRadius: 'Large'})}>
          <List.Accessory>
            {banner.image_path ? (
              <img
                src={`${getBackendUrl()}${banner.image_path}`}
                alt=""
                className={styles.listThumb}
              />
            ) : (
              <Box
                className={styles.listThumb}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <UIIcon src={ImageLine} size="small" className={atoms({color: 'Text Subtle'})} />
              </Box>
            )}
          </List.Accessory>
          <List.Content>
            <LabelGroup
              withText={<Text isBold>{banner.title}</Text>}
              withSubtext={
                <Text size="small" color="subtle">
                  {formatSchedule(banner.starts_at, banner.ends_at)} ·{' '}
                  {scheduleZoneLabel(banner.use_local_time)}
                </Text>
              }
            />
          </List.Content>
          <List.Accessory>
            <Split gap="8" alignY="center">
              {banner.fte_only && (
                <Split.Item>
                  <Badge variant="standard">FTE only</Badge>
                </Split.Item>
              )}
              <Split.Item>
                <Badge variant={status.variant}>{status.label}</Badge>
              </Split.Item>
              <Split.Item>
                <Button
                  variant="opacity"
                  size="small"
                  withIconStart={<UIIcon src={EditLine} />}
                  aria-label={`Edit ${banner.title}`}
                  onClick={() => onEdit(banner)}
                >
                  Edit
                </Button>
              </Split.Item>
            </Split>
          </List.Accessory>
        </List.Item>
      );
    })}
  </List>
);
